import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/ApiService';
import '../styles/OrcamentosList.css';
import { FiSettings, FiPlus, FiSearch, FiFileText, FiClock, FiCheckCircle, FiChevronDown } from 'react-icons/fi';
import { FormModal } from './ModalAlert';
import Header from './Header';

const OrcamentosList = () => {
  const { currentUser, logout } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const { darkMode, toggleTheme } = useTheme();
  
  // Estados para dropdown do usuário
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  
  // Estados para modal de configuração
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configDropdownOpen, setConfigDropdownOpen] = useState(false);
  const configDropdownRef = useRef(null);
  const [configData, setConfigData] = useState({
    descricao: '',
    maoDeObraCusto: 0,
    fatorMultiplicacao: 1.7
  });
  const [configs, setConfigs] = useState([]);
  const [isEditingConfig, setIsEditingConfig] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState(null);
  
  // Fechar dropdowns quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Fechar dropdown de usuário
      if (showUserDropdown && !event.target.closest('.user-dropdown-container')) {
        setShowUserDropdown(false);
      }
      
      // Fechar dropdown de configurações
      if (configDropdownOpen && configDropdownRef.current && !configDropdownRef.current.contains(event.target)) {
        setConfigDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown, configDropdownOpen]);

  
  // Função para fazer logout
  const handleLogout = () => {
    setShowUserDropdown(false);
    logout();
    navigate('/');
  };
  
  // Estados para orçamentos
  const [orcamentos, setOrcamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estados para modal de novo orçamento
  const [newOrcamentoModalOpen, setNewOrcamentoModalOpen] = useState(false);
  const [newOrcamentoData, setNewOrcamentoData] = useState({
    name: '',
    cliente: '',
    enderecoObra: '',
    formaPagamento: '',
    descricao: ''
  });
  const [error, setError] = useState('');
  
  // Estado para modal de confirmação de deleção
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [orcamentoToDelete, setOrcamentoToDelete] = useState(null);

  // Adicionar novos estados para busca de cliente
  const [clientesList, setClientesList] = useState([]);
  const [clientesFiltrados, setClientesFiltrados] = useState([]);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [mostrarListaClientes, setMostrarListaClientes] = useState(false);

  // Novo estado para a edição de orçamento
  const [editOrcamentoModalOpen, setEditOrcamentoModalOpen] = useState(false);
  const [editingOrcamento, setEditingOrcamento] = useState(null);
  const [editOrcamentoData, setEditOrcamentoData] = useState({
    name: '',
    cliente: '',
    status: ''
  });

  // Carregar orçamentos e configurações ao iniciar
  useEffect(() => {
    loadOrcamentos();
    
    // Como loadConfigs agora é uma função assíncrona, precisamos chamar dentro de uma IIFE
    (async () => {
      try {
        await loadConfigs();
      } catch (error) {
        console.error("Erro ao carregar configurações:", error);
      }
    })();
  }, []);

  // Função para carregar orçamentos
  const loadOrcamentos = async () => {
    try {
      setLoading(true);
      const result = await apiService.getOrcamentos();
      
      if (result.success && result.data && Array.isArray(result.data.orcamentos)) {
        setOrcamentos(result.data.orcamentos);
      } else {
        showNotification('error', 'Não foi possível carregar os orçamentos');
        setOrcamentos([]);
      }
    } catch (error) {
      showNotification('error', 'Erro ao buscar dados de orçamentos');
      setOrcamentos([]);
    } finally {
      setLoading(false);
    }
  };

  // Função para normalizar string de empresa (para comparações mais robustas)
  const normalizeEmpresa = (empresa) => {
    if (!empresa) return '';
    return empresa.toString().trim().toLowerCase();
  };

  // Função para verificar se a configuração pertence à empresa do usuário
  const isConfigFromUserCompany = (config) => {
    if (!config) return false;
    
    // Admin sempre vê todas as configurações
    if (currentUser?.role === 'admin') return true;
    
    const empresaUsuario = normalizeEmpresa(currentUser?.empresa);
    const empresaConfig = normalizeEmpresa(config.empresa);
    
    // Se a config não tem empresa definida, consideramos que é visível para todos
    if (!empresaConfig) return true;
    
    // Comparação exata entre as empresas normalizadas
    return empresaConfig === empresaUsuario;
  };

  // Função para carregar configurações
  const loadConfigs = async () => {
    try {
      const empresaUsuario = normalizeEmpresa(currentUser?.empresa || 'kmf');
      
      const result = await apiService.getConfiguracao();
      
      if (result.success && 
          (!result.data || 
           !result.data.orcamentoConfigs || 
           !Array.isArray(result.data.orcamentoConfigs) ||
           result.data.orcamentoConfigs.length === 0)) {
        
        const novaConfig = {
          empresa: empresaUsuario,
          empresaID: `${empresaUsuario}_a36c4d`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          orcamentoConfigs: []
        };
        
        const saveResult = await apiService.updateConfiguracao(novaConfig);
        
        if (saveResult.success) {
          setConfigs([]);
          localStorage.setItem('orcamentoConfigs', JSON.stringify([]));
          return;
        }
      }
      
      if (result.success && result.data && result.data.orcamentoConfigs && Array.isArray(result.data.orcamentoConfigs)) {
        const configsValidadas = result.data.orcamentoConfigs.map(config => ({
          id: config.id || Date.now(),
          descricao: config.descricao || '',
          maoDeObraCusto: typeof config.maoDeObraCusto === 'number' ? config.maoDeObraCusto : 0,
          fatorMultiplicacao: typeof config.fatorMultiplicacao === 'number' ? config.fatorMultiplicacao : 1.7,
          empresa: config.empresa || currentUser?.empresa || 'kmf'
        }));
        
        const configsFiltradas = configsValidadas.filter(isConfigFromUserCompany);
        
        setConfigs(configsFiltradas);
        localStorage.setItem('orcamentoConfigs', JSON.stringify(configsValidadas));
        return;
      }
      
      const savedConfigs = localStorage.getItem('orcamentoConfigs');
      if (savedConfigs) {
        try {
          const parsedConfigs = JSON.parse(savedConfigs);
          
          const configsFiltradas = parsedConfigs.filter(isConfigFromUserCompany);
          
          setConfigs(configsFiltradas);
          
          if (parsedConfigs.length > 0) {
            await syncConfigsToAPI(parsedConfigs);
          }
        } catch (parseError) {
          setConfigs([]);
        }
      }
    } catch (error) {
      try {
        const savedConfigs = localStorage.getItem('orcamentoConfigs');
        if (savedConfigs) {
          const parsedConfigs = JSON.parse(savedConfigs);
          
          const configsFiltradas = parsedConfigs.filter(isConfigFromUserCompany);
          
          setConfigs(configsFiltradas);
        }
      } catch (localError) {
        setConfigs([]);
      }
    }
  };

  // Função para sincronizar configurações do localStorage para a API
  const syncConfigsToAPI = async (configsToSync) => {
    try {
      const result = await apiService.getConfiguracao();
      
      if (result.success && result.data) {
        const configsComEmpresa = configsToSync.map(config => ({
          ...config,
          empresa: currentUser?.empresa || 'kmf',
          descricao: config.descricao || '',
          fatorMultiplicacao: config.fatorMultiplicacao || 1.7,
          maoDeObraCusto: config.maoDeObraCusto || 0,
          id: config.id
        }));
        
        const updatedConfig = {
          ...result.data,
          orcamentoConfigs: configsComEmpresa
        };
        
        await apiService.updateConfiguracao(updatedConfig);
      }
    } catch (error) {
      showNotification('error', 'Erro ao sincronizar configurações');
    }
  };

  // Função para salvar configurações
  const saveConfigs = async (newConfigs) => {
    try {
      localStorage.setItem('orcamentoConfigs', JSON.stringify(newConfigs));
      setConfigs(newConfigs);
      
      const configsComEmpresa = newConfigs.map(config => ({
        ...config,
        empresa: currentUser?.empresa || 'kmf',
        descricao: config.descricao || '',
        fatorMultiplicacao: config.fatorMultiplicacao || 1.7,
        maoDeObraCusto: config.maoDeObraCusto || 0,
        id: config.id
      }));
      
      const result = await apiService.getConfiguracao();
      
      if (result.success && result.data) {
        const updatedConfig = {
          ...result.data,
          orcamentoConfigs: configsComEmpresa
        };
        
        await apiService.updateConfiguracao(updatedConfig);
      }
    } catch (error) {
      showNotification('error', 'Erro ao salvar configurações');
    }
  };

  // Função para adicionar nova configuração
  const handleAddConfig = async () => {
    if (!configData.descricao.trim()) {
      showNotification('error', 'A descrição é obrigatória');
      return;
    }

    // Se estiver editando, atualizar a configuração existente
    if (isEditingConfig && editingConfigId) {
      const updatedConfigs = configs.map(config => 
        config.id === editingConfigId ? { ...config, ...configData } : config
      );
      
      await saveConfigs(updatedConfigs);
      
      // Resetar o estado para adição
      setIsEditingConfig(false);
      setEditingConfigId(null);
    } else {
      // Caso contrário, adicionar nova configuração
      const newConfig = {
        id: Date.now(),
        ...configData
      };

      const updatedConfigs = [...configs, newConfig];
      await saveConfigs(updatedConfigs);
    }
    
    // Resetar o formulário
    setConfigData({
      descricao: '',
      maoDeObraCusto: 0,
      fatorMultiplicacao: 1.7
    });
    setConfigModalOpen(false);
  };

  // Função para iniciar a edição de uma configuração
  const handleEditConfig = (config, e) => {
    e.stopPropagation(); // Impedir que outras ações ocorram
    
    // Preencher o formulário com os dados da configuração selecionada
    setConfigData({
      descricao: config.descricao,
      maoDeObraCusto: config.maoDeObraCusto,
      fatorMultiplicacao: config.fatorMultiplicacao
    });
    
    // Configurar o estado para edição
    setIsEditingConfig(true);
    setEditingConfigId(config.id);
    
    // Fechar o dropdown e abrir o modal
    setConfigDropdownOpen(false);
    setConfigModalOpen(true);
  };

  // Função para remover configuração
  const handleRemoveConfig = async (configId) => {
    const updatedConfigs = configs.filter(config => config.id !== configId);
    
    // As configurações são salvas automaticamente na API e no localStorage
    await saveConfigs(updatedConfigs);
  };

  // Função para selecionar uma configuração para o novo orçamento
  const handleSelectConfig = (config) => {
    setNewOrcamentoData({
      ...newOrcamentoData,
      descricao: config.descricao
    });
    setConfigDropdownOpen(false);
    setNewOrcamentoModalOpen(true);
    showNotification('info', `Configuração "${config.descricao}" selecionada`);
  };

  // Função para remover acentos e caracteres especiais
  const removeAcentos = (texto) => {
    if (!texto) return '';
    
    // Substituir acentos por caracteres sem acento
    const comAcento = 'áàãâäéèêëíìîïóòõôöúùûüçÁÀÃÂÄÉÈÊËÍÌÎÏÓÒÕÖÔÚÙÛÜÇ';
    const semAcento = 'aaaaaeeeeiiiiooooouuuucAAAAAEEEEIIIIOOOOOUUUUC';
    
    let result = texto;
    for (let i = 0; i < comAcento.length; i++) {
      result = result.replace(new RegExp(comAcento[i], 'g'), semAcento[i]);
    }
    
    // Substituir espaços por traços e remover caracteres especiais
    return result
      .trim()
      .replace(/\s+/g, '-')        // Substituir espaços por traços
      .replace(/[^a-zA-Z0-9-]/g, '') // Manter apenas letras, números e traços
      .toLowerCase();              // Converter para minúsculas
  };

  // Função para abrir orçamento selecionado
  const handleOpenOrcamento = (orcamento) => {
    if (orcamento && orcamento.cliente) {
      // Normalizar o nome do cliente removendo acentos e caracteres especiais
      const clienteNormalizado = removeAcentos(orcamento.cliente);
      navigate(`/orcamento/${clienteNormalizado}`);
    }
  };

  // Função para abrir modal de novo orçamento
  const handleOpenNewOrcamentoModal = () => {
    setNewOrcamentoData({
      name: '',
      cliente: '',
      enderecoObra: '',
      formaPagamento: '',
      descricao: configData.descricao || ''
    });
    setError('');
    setClientesFiltrados([]);
    setMostrarListaClientes(false);
    setNewOrcamentoModalOpen(true);
    
    // Carregar clientes para a busca
    carregarClientes();
  };
  
  // Função para carregar clientes
  const carregarClientes = async () => {
    try {
      const result = await apiService.getClientes();
      if (result.success && result.data && Array.isArray(result.data.clientes)) {
        setClientesList(result.data.clientes);
      } else {
        setClientesList([]);
      }
    } catch (error) {
      setClientesList([]);
    }
  };
  
  // Função para buscar cliente pelo nome
  const buscarCliente = (nome) => {
    if (!nome || nome.trim().length < 2 || !clientesList.length) {
      setClientesFiltrados([]);
      setMostrarListaClientes(false);
      return;
    }
    
    setBuscandoCliente(true);
    
    // Remover acentos e converter para minúsculas para a busca
    const normalizedNome = removeAcentos(nome.trim().toLowerCase());
    
    const filtrados = clientesList.filter(cliente => 
      removeAcentos(cliente.nome?.toLowerCase() || '').includes(normalizedNome)
    );
    
    setClientesFiltrados(filtrados);
    setMostrarListaClientes(filtrados.length > 0);
    setBuscandoCliente(false);
  };
  
  // Atualizar campos quando o usuário digitar o nome do cliente
  const handleClienteChange = (e) => {
    const valor = e.target.value;
    setNewOrcamentoData({...newOrcamentoData, cliente: valor});
    
    // Buscar cliente após digitar
    if (valor.length >= 2) {
      buscarCliente(valor);
    } else {
      setClientesFiltrados([]);
      setMostrarListaClientes(false);
    }
  };
  
  // Função para selecionar um cliente da lista
  const selecionarCliente = (cliente) => {
    setNewOrcamentoData(prev => ({
      ...prev,
      cliente: cliente.nome,
      enderecoObra: cliente.endereco || prev.enderecoObra
    }));
    
    setMostrarListaClientes(false);
  };

  // Função para criar novo orçamento
  const handleCreateOrcamento = async () => {
    if (!newOrcamentoData.name.trim()) {
      setError('O nome do orçamento é obrigatório');
      return;
    }
    
    if (!newOrcamentoData.cliente.trim()) {
      setError('O nome do cliente é obrigatório');
      return;
    }
    
    try {
      setLoading(true);
      
      // Criar o objeto de dados
      const orcamentoData = {
        name: newOrcamentoData.name.trim(),
        empresa: currentUser?.empresa || 'kmf',
        role: currentUser?.role || 'user',
        status: 'Novo',
        descricao: newOrcamentoData.descricao.trim() || '',
        cliente: newOrcamentoData.cliente.trim(),
        enderecoObra: newOrcamentoData.enderecoObra.trim() || '',
        formaPagamento: newOrcamentoData.formaPagamento.trim() || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      const result = await apiService.createOrcamento(orcamentoData);
      
      if (result.success) {
        showNotification('success', `Orçamento "${result.data.name}" criado com sucesso!`);
        setNewOrcamentoModalOpen(false);
        
        // Recarregar a página sem cache
        refreshPageWithoutCache();
      } else {
        setError(result.error || 'Erro ao criar orçamento');
      }
    } catch (error) {
      showNotification('error', `Erro ao criar orçamento: ${error.message || 'Erro desconhecido'}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Função para recarregar a página sem cache
  const refreshPageWithoutCache = () => {
    const timestamp = new Date().getTime();
    const refreshUrl = window.location.pathname + '?t=' + timestamp;
    window.location.href = refreshUrl;
  };

  // Função para remover acentos, normalizar e padronizar status
  const normalizarStatus = (status) => {
    if (!status) return 'novo';
    return status
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos corretamente
      .replace(/-/g, ' ') // Troca hífens por espaço
      .replace(/\s+/g, ' ') // Remove espaços duplicados
      .trim()
      .toLowerCase();
  };

  // Definir ordem de prioridade dos status (tudo minúsculo e sem acento)
  const statusPriority = {
    'em andamento': 1,
    'novo': 2,
    'aprovado': 3,
    'finalizado': 4,
    'rejeitado': 5
  };

  // Função para ordenar orçamentos por status
  const ordenarPorStatus = (orcamentos) => {
    return [...orcamentos].sort((a, b) => {
      const statusA = normalizarStatus(a.status || 'Novo');
      const statusB = normalizarStatus(b.status || 'Novo');
      const prioridadeA = statusPriority[statusA] || 999;
      const prioridadeB = statusPriority[statusB] || 999;
      if (prioridadeA !== prioridadeB) {
        return prioridadeA - prioridadeB;
      }
      const dataA = new Date(a.createdAt || 0);
      const dataB = new Date(b.createdAt || 0);
      return dataB - dataA;
    });
  };

  // Filtrar e ordenar orçamentos
  const filteredOrcamentos = ordenarPorStatus(
    orcamentos.filter(orc => 
      orc.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      orc.cliente?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // Função para renderizar o ícone do status
  const renderStatusIcon = (status) => {
    const statusLower = (status || 'novo').toLowerCase();
    switch (statusLower) {
      case 'em andamento':
      case 'em-andamento':
        return <i className="fas fa-hourglass-half status-icon status-em-andamento"></i>;
      case 'finalizado':
        return <i className="fas fa-check-circle status-icon status-finalizado"></i>;
      case 'aprovado':
        return <i className="fas fa-thumbs-up status-icon status-aprovado"></i>;
      case 'rejeitado':
        return <i className="fas fa-thumbs-down status-icon status-rejeitado"></i>;
      default:
        return <i className="fas fa-file-invoice-dollar status-icon status-novo"></i>;
    }
  };

  // Função para formatação de data
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR');
    } catch {
      return 'Data inválida';
    }
  };

  // Função para excluir orçamento
  const handleDeleteOrcamento = async (orcamento) => {
    if (!orcamento?.id || !orcamento?.name) {
      showNotification('error', 'Dados do orçamento inválidos');
      return;
    }

    // Abrir modal de confirmação
    setOrcamentoToDelete(orcamento);
    setDeleteModalOpen(true);
  };

  // Função para confirmar a deleção do orçamento
  const confirmDeleteOrcamento = async () => {
    if (!orcamentoToDelete) return;
    
    try {
      setLoading(true);
      
      // Tentar excluir o orçamento
      const result = await apiService.deleteOrcamento(orcamentoToDelete.id);
      
      if (result.success) {
        showNotification('success', `Orçamento "${orcamentoToDelete.name}" excluído com sucesso!`);
        
        // Atualizar a lista localmente antes de recarregar
        setOrcamentos(prevOrcamentos => 
          prevOrcamentos.filter(orc => orc.id !== orcamentoToDelete.id)
        );
        
        // Fechar a modal
        setDeleteModalOpen(false);
        setOrcamentoToDelete(null);
        
        // Recarregar a página para garantir sincronização
        setTimeout(() => {
          refreshPageWithoutCache();
        }, 1000);
      } else {
        console.error('Falha ao excluir orçamento:', result);
        
        let errorMessage = 'Não foi possível excluir o orçamento.';
        
        // Verificar se há detalhes específicos do erro
        if (result.details?.error) {
          errorMessage += ` Motivo: ${result.details.error}`;
        } else if (result.error) {
          errorMessage += ` Motivo: ${result.error}`;
        }
        
        showNotification('error', errorMessage);
      }
    } catch (error) {
      console.error('Erro ao excluir orçamento:', error);
      showNotification('error', 'Erro inesperado ao tentar excluir o orçamento. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  // Nova função para abrir o modal de edição de orçamento
  const handleEditOrcamento = (orcamento, e) => {
    e.stopPropagation();
    setEditingOrcamento(orcamento);
    setEditOrcamentoData({
      name: orcamento.name || '',
      cliente: orcamento.cliente || '',
      status: orcamento.status || 'Novo'
    });
    setEditOrcamentoModalOpen(true);
  };

  // Nova função para salvar as alterações do orçamento
  const handleSaveOrcamentoEdit = async () => {
    if (!editingOrcamento || !editOrcamentoData.name.trim() || !editOrcamentoData.cliente.trim()) {
      showNotification('error', 'Nome e cliente são obrigatórios');
      return;
    }

    try {
      setLoading(true);

      const updatedOrcamento = {
        ...editingOrcamento,
        name: editOrcamentoData.name.trim(),
        cliente: editOrcamentoData.cliente.trim(),
        status: editOrcamentoData.status || 'Novo',
        updatedAt: new Date().toISOString()
      };

      const result = await apiService.updateOrcamento(editingOrcamento.id, updatedOrcamento);

      if (result.success) {
        const updatedOrcamentos = orcamentos.map(orc => 
          orc.id === editingOrcamento.id ? updatedOrcamento : orc
        );
        setOrcamentos(updatedOrcamentos);
        
        setEditOrcamentoModalOpen(false);
        showNotification('success', 'Orçamento atualizado com sucesso!');
      } else {
        showNotification('error', `Erro ao atualizar orçamento: ${result.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      showNotification('error', 'Erro ao atualizar orçamento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`orcamentos-container ${darkMode ? 'dark-mode' : ''}`}>
      <Header 
        onLogout={handleLogout}
        onAction={(action, message) => {
          if (action === 'info') {
            showNotification('info', message);
          }
        }}
      />
      
      <div className="orcamentos-content">
        <div className="orcamentos-header">
          <div className="orcamentos-header-left">
            <h1>Gerenciar Orçamentos</h1>
            <div className="config-dropdown-container" ref={configDropdownRef}>
              <button 
                className="config-button" 
                onClick={() => navigate('/configuracao-conta')}
                title="Configurações de Orçamento"
              >
                <i className="fas fa-cog"></i>
                <span>Configurações</span>
              </button>
            </div>
          </div>
          <div className="orcamentos-header-right">
            <button 
              className="new-orcamento-button"
              onClick={handleOpenNewOrcamentoModal}
            >
              <FiPlus />
              <span>Novo Orçamento</span>
            </button>
          </div>
        </div>
        
        <div className="search-container">
          <div className="search-input-wrapper">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Buscar orçamentos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
        
        <div className="orcamentos-list-wrapper">
          {loading ? (
            <div className="loading-container">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Carregando orçamentos...</p>
            </div>
          ) : filteredOrcamentos.length > 0 ? (
            <div className="orcamentos-grid">
              {filteredOrcamentos.map(orcamento => (
                <div 
                  key={orcamento.id} 
                  className="orcamento-card"
                >
                  <div className="orcamento-header">
                    <div className="orcamento-header-left">
                      {renderStatusIcon(orcamento.status)}
                      <span className={`status-badge status-${orcamento.status?.toLowerCase().replace(/\s+/g, '-') || 'novo'}`}>
                        {orcamento.status || 'Novo'}
                      </span>
                    </div>
                    <div className="orcamento-actions">
                      <button 
                        className="orcamento-action-btn edit-btn"
                        onClick={(e) => handleEditOrcamento(orcamento, e)}
                        title="Editar orçamento"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button 
                        className="orcamento-action-btn delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteOrcamento(orcamento);
                        }}
                        title="Excluir orçamento"
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                  <div 
                    className="orcamento-content"
                    onClick={() => handleOpenOrcamento(orcamento)}
                  >
                    <h3 className="orcamento-title">{orcamento.name}</h3>
                    <p className="orcamento-cliente">{orcamento.cliente}</p>
                    <div className="orcamento-footer">
                      <span className="orcamento-date">
                        Criado em: {formatDate(orcamento.createdAt)}
                      </span>
                      <span className="orcamento-value">
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        }).format(orcamento.valorTotal || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-orcamentos">
              <i className="fas fa-file-invoice-dollar"></i>
              <p>Nenhum orçamento encontrado</p>
              <button 
                className="create-first-button"
                onClick={handleOpenNewOrcamentoModal}
              >
                Criar seu primeiro orçamento
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Modal de configurações */}
      <FormModal
        isOpen={configModalOpen}
        title={isEditingConfig ? "Editar Configuração" : "Nova Configuração"}
        onClose={() => {
          setConfigModalOpen(false);
          setIsEditingConfig(false);
          setEditingConfigId(null);
          setConfigData({
            descricao: '',
            maoDeObraCusto: 0,
            fatorMultiplicacao: 1.7
          });
        }}
        onConfirm={handleAddConfig}
        confirmText={isEditingConfig ? "Salvar Alterações" : "Salvar Configuração"}
      >
        <div className="form-group">
          <label htmlFor="descricao">Descrição*</label>
          <input
            type="text"
            id="descricao"
            name="descricao"
            value={configData.descricao}
            onChange={(e) => setConfigData({...configData, descricao: e.target.value})}
            placeholder="Digite uma descrição para esta configuração"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="maoDeObraCusto">Valor de Mão de Obra Preço de Custo (R$)</label>
          <input
            type="number"
            id="maoDeObraCusto"
            name="maoDeObraCusto"
            value={configData.maoDeObraCusto}
            onChange={(e) => setConfigData({...configData, maoDeObraCusto: parseFloat(e.target.value) || 0})}
            min="0"
            step="0.01"
            placeholder="Ex: 40.00"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="fatorMultiplicacao">Fator de Multiplicação para Lucro</label>
          <input
            type="number"
            id="fatorMultiplicacao"
            name="fatorMultiplicacao"
            value={configData.fatorMultiplicacao}
            onChange={(e) => setConfigData({...configData, fatorMultiplicacao: parseFloat(e.target.value) || 1.7})}
            min="1"
            step="0.1"
            placeholder="Ex: 1.7 (70% de lucro)"
          />
          <small className="form-text">
            Para 70% de lucro, use 1.7 (multiplica o valor de custo por 1.7)
          </small>
        </div>
      </FormModal>
      
      {/* Modal de novo orçamento */}
      <FormModal
        isOpen={newOrcamentoModalOpen}
        title="Novo Orçamento"
        onClose={() => setNewOrcamentoModalOpen(false)}
        onConfirm={handleCreateOrcamento}
        confirmText="Criar Orçamento"
      >
        <div className="form-group">
          <label htmlFor="orcamentoCliente">Cliente*</label>
          <div className="cliente-search-container">
            <input
              type="text"
              id="orcamentoCliente"
              value={newOrcamentoData.cliente}
              onChange={handleClienteChange}
              placeholder="Digite o nome do cliente"
              autoComplete="off"
            />
            {buscandoCliente && <div className="cliente-buscando">Buscando cliente...</div>}
            
            {mostrarListaClientes && (
              <div className="clientes-dropdown">
                {clientesFiltrados.map((cliente, index) => (
                  <div 
                    key={cliente.id || index} 
                    className="cliente-option" 
                    onClick={() => selecionarCliente(cliente)}
                  >
                    {cliente.nome}
                    {cliente.endereco && <span className="cliente-endereco">{cliente.endereco}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="orcamentoName">Nome do Orçamento*</label>
          <input
            type="text"
            id="orcamentoName"
            value={newOrcamentoData.name}
            onChange={(e) => setNewOrcamentoData({...newOrcamentoData, name: e.target.value})}
            placeholder="Digite o nome do orçamento"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="orcamentoEndereco">Endereço da Obra</label>
          <input
            type="text"
            id="orcamentoEndereco"
            value={newOrcamentoData.enderecoObra}
            onChange={(e) => setNewOrcamentoData({...newOrcamentoData, enderecoObra: e.target.value})}
            placeholder="Digite o endereço da obra"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="orcamentoPagamento">Forma de Pagamento</label>
          <input
            type="text"
            id="orcamentoPagamento"
            value={newOrcamentoData.formaPagamento}
            onChange={(e) => setNewOrcamentoData({...newOrcamentoData, formaPagamento: e.target.value})}
            placeholder="Digite a forma de pagamento"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="orcamentoDescricao">Descrição do Orçamento</label>
          <textarea
            id="orcamentoDescricao"
            value={newOrcamentoData.descricao}
            onChange={(e) => setNewOrcamentoData({...newOrcamentoData, descricao: e.target.value})}
            placeholder="Digite uma descrição para o orçamento"
            rows="3"
          ></textarea>
        </div>
        
        {error && <div className="error-message">{error}</div>}
      </FormModal>

      {/* Modal de edição de orçamento */}
      <FormModal
        isOpen={editOrcamentoModalOpen}
        title="Editar Orçamento"
        onClose={() => setEditOrcamentoModalOpen(false)}
        onConfirm={handleSaveOrcamentoEdit}
        confirmText="Salvar Alterações"
      >
        <div className="form-group">
          <label htmlFor="editOrcamentoName">Nome do Orçamento*</label>
          <input
            type="text"
            id="editOrcamentoName"
            value={editOrcamentoData.name}
            onChange={(e) => setEditOrcamentoData({...editOrcamentoData, name: e.target.value})}
            placeholder="Digite o nome do orçamento"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="editOrcamentoCliente">Cliente*</label>
          <input
            type="text"
            id="editOrcamentoCliente"
            value={editOrcamentoData.cliente}
            onChange={(e) => setEditOrcamentoData({...editOrcamentoData, cliente: e.target.value})}
            placeholder="Digite o nome do cliente"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="editOrcamentoStatus">Status</label>
          <select
            id="editOrcamentoStatus"
            value={editOrcamentoData.status}
            onChange={(e) => setEditOrcamentoData({...editOrcamentoData, status: e.target.value})}
            className="status-select"
          >
            <option value="Novo">Novo</option>
            <option value="Em Andamento">Em Andamento</option>
            <option value="Finalizado">Finalizado</option>
            <option value="Aprovado">Aprovado</option>
            <option value="Rejeitado">Rejeitado</option>
          </select>
        </div>
      </FormModal>

      {/* Modal de confirmação de deleção */}
      <FormModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setOrcamentoToDelete(null);
        }}
        title="Confirmar Exclusão"
        hideFooter={true}
      >
        <div className="delete-confirmation-modal">
          <div className="modal-icon">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <div className="modal-content">
            <h3>Tem certeza que deseja excluir este orçamento?</h3>
            <p>
              Você está prestes a excluir o orçamento "{orcamentoToDelete?.name}".
              Esta ação não poderá ser desfeita.
            </p>
            <div className="modal-details">
              <p><strong>Cliente:</strong> {orcamentoToDelete?.cliente || 'N/A'}</p>
              <p><strong>Status:</strong> {orcamentoToDelete?.status || 'N/A'}</p>
              <p><strong>Data de Criação:</strong> {formatDate(orcamentoToDelete?.createdAt)}</p>
            </div>
          </div>
          <div className="modal-actions">
            <button
              className="cancel-button"
              onClick={() => {
                setDeleteModalOpen(false);
                setOrcamentoToDelete(null);
              }}
            >
              Cancelar
            </button>
            <button
              className="delete-button"
              onClick={confirmDeleteOrcamento}
            >
              Confirmar Exclusão
            </button>
          </div>
        </div>
      </FormModal>
    </div>
  );
};

export default OrcamentosList; 